import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

// === COMPONENTES DO MATERIAL-UI ===
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Tooltip from "@material-ui/core/Tooltip";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

// === ESTILIZAÇÃO JSS ===
const useStyles = makeStyles(theme => ({
    ticket: {
        position: "relative",
    },
    pendingTicket: {
        cursor: "unset",
    },
    contactNameWrapper: {
        display: "flex",
        justify: "space-between",
        alignItems: "center",
    },
    lastMessageTime: {
        justifySelf: "flex-end",
    },
    closedBadge: {
        alignSelf: "center",
        justifySelf: "flex-end",
        marginRight: 32,
        marginLeft: "auto",
    },
    contactLastMessage: {
        paddingRight: 10,
    },
    // Estilo para forçar a exibição do Badge de mensagens não lidas
    newMessagesBadge: {
        alignSelf: "center",
        marginRight: 8,
        marginLeft: "auto",
        "& .MuiBadge-badge": {
            backgroundColor: "#25D366", // Verde WhatsApp
            color: "#FFF",
            fontWeight: "bold",
            fontSize: "0.75rem",
            position: "static", // Evita sobreposição do avatar/texto
            transform: "none"
        }
    },
    acceptButton: {
        position: "absolute",
        left: "50%",
    },
    ticketQueueColor: {
        flex: "none",
        width: "8px",
        height: "100%",
        position: "absolute",
        top: "0%",
        left: "0%",
    },
    userTag: {
        position: "absolute",
        marginRight: 5,
        right: 5,
        bottom: 5,
        background: "#2576D2",
        color: "#ffffff",
        border: "1px solid #CCC",
        padding: "1px 5px",
        borderRadius: 10,
        fontSize: "0.8em"
    },
}));

const TicketListItem = ({ ticket }) => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { user } = useContext(AuthContext);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleAcepptTicket = async id => {
        setLoading(true);
        try {
            await api.put(`/tickets/${id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${id}`);
    };

    const handleSelectTicket = id => {
        history.push(`/tickets/${id}`);
    };

    // Avalia o contador vindo do backend
    const unreadCount = Number(ticket.unreadMessages) || 0;
    const hasUnread = unreadCount > 0;

    return (
        <React.Fragment key={ticket.id}>
            <ListItem
                dense
                button
                onClick={e => {
                    if (ticket.status === "pending") return;
                    handleSelectTicket(ticket.id);
                }}
                selected={ticketId && +ticketId === ticket.id}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                })}
            >
                {/* Barra de cor da Fila */}
                <Tooltip arrow placement="right" title={ticket.queue?.name || "Sem fila"}>
                    <span
                        style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
                        className={classes.ticketQueueColor}
                    ></span>
                </Tooltip>

                {/* Avatar */}
                <ListItemAvatar>
                    <Avatar src={ticket?.contact?.profilePicUrl} />
                </ListItemAvatar>

                {/* Conteúdo do Item */}
                <ListItemText
                    disableTypography
                    primary={
                        <span className={classes.contactNameWrapper}>
                            <Typography
                                noWrap
                                component="span"
                                variant="body2"
                                style={{
                                    fontWeight: hasUnread ? 700 : 500,
                                    color: hasUnread ? "#000000" : "inherit"
                                }}
                            >
                                {ticket.contact?.name}
                            </Typography>

                            {ticket.status === "closed" && (
                                <Badge
                                    className={classes.closedBadge}
                                    badgeContent={"closed"}
                                    color="primary"
                                />
                            )}

                            {ticket.lastMessage && (
                                <Typography
                                    className={classes.lastMessageTime}
                                    component="span"
                                    variant="body2"
                                    style={{
                                        color: hasUnread ? "#25D366" : "#888",
                                        fontWeight: hasUnread ? "bold" : "normal"
                                    }}
                                >
                                    {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                                        <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                                    ) : (
                                        <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                                    )}
                                </Typography>
                            )}

                            {ticket.whatsappId && (
                                <div className={classes.userTag} title={i18n.t("ticketsList.connectionTitle")}>
                                    {ticket.whatsapp?.name}
                                </div>
                            )}
                        </span>
                    }
                    secondary={
                        <span className={classes.contactNameWrapper} style={{ flexDirection: "column", alignItems: "flex-start" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography
                                    className={classes.contactLastMessage}
                                    noWrap
                                    component="span"
                                    variant="body2"
                                    style={{
                                        fontWeight: hasUnread ? 700 : 400,
                                        color: hasUnread ? "#111b21" : "#667781"
                                    }}
                                >
                                    {ticket.lastMessage ? (
                                        <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
                                    ) : (
                                        <br />
                                    )}
                                </Typography>

                                {/* BALÃO DE NOTIFICAÇÃO DO MATERIAL-UI */}
                                {hasUnread && (
                                    <Badge
                                        className={classes.newMessagesBadge}
                                        badgeContent={unreadCount}
                                        overlap="rectangular"
                                    />
                                )}
                            </div>

                            {/* Tag do Atendente */}
                            {ticket.user && (
                                <div style={{ marginTop: "4px" }}>
                                    <span
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 700,
                                            backgroundColor: "#fbff00",
                                            color: "#211802",
                                            border: "1px solid #ffe082",
                                            padding: "2px 8px",
                                            borderRadius: "12px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "4px"
                                        }}
                                    >
                                        <span style={{
                                            width: "6px",
                                            height: "6px",
                                            borderRadius: "50%",
                                            backgroundColor: "#fd3c3c",
                                            display: "inline-block"
                                        }} />
                                        Atendente: {ticket.user.name}
                                    </span>
                                </div>
                            )}
                        </span>
                    }
                />

                {ticket.status === "pending" && (
                    <ButtonWithSpinner
                        color="primary"
                        variant="contained"
                        className={classes.acceptButton}
                        size="small"
                        loading={loading}
                        onClick={e => handleAcepptTicket(ticket.id)}
                    >
                        {i18n.t("ticketsList.buttons.accept")}
                    </ButtonWithSpinner>
                )}
            </ListItem>
            <Divider variant="inset" component="li" />
        </React.Fragment>
    );
};

export default TicketListItem;